if (!customElements.get('sa-line-popup')) {
  class SaLinePopup extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleNoShow = this.handleNoShow.bind(this);
      this._scrollTimer = null;
      this._lastFocused = null;
      this._openedByHover = false;
      this._hoverOpenTimer = null;
      this._hoverCloseTimer = null;
    }

    connectedCallback() {
      this.trigger = this.querySelector('.sa-line-popup__trigger');
      this.triggerBtn = this.querySelector('.sa-line-popup__btn');
      this.modal = this.querySelector('.sa-line-popup__modal');
      this.overlay = this.querySelector('.sa-line-popup__overlay');
      this.closeBtn = this.querySelector('.sa-line-popup__close');
      this.noShowInput = this.querySelector('.sa-line-popup__no-show-input');

      const storageKey = `sa-line-popup-hidden-${this.dataset.sectionId}`;
      if (sessionStorage.getItem(storageKey) === 'true' && !window.Shopify?.designMode) {
        return;
      }

      if (this.trigger) this.trigger.style.display = '';

      if (this.triggerBtn) {
        // Click: open in persistent mode (mouseleave won't close)
        this.triggerBtn.addEventListener('click', () => {
          // If already open via hover, switch to click (persistent) mode
          if (this.modal?.classList.contains('sa-line-popup__modal--open')) {
            this._openedByHover = false;
            this._clearHoverCloseTimer();
            return;
          }
          this._openedByHover = false;
          this.openModal();
        });

        // Hover: desktop only, opens modal in hover mode
        this.triggerBtn.addEventListener('mouseenter', () => {
          if (!this._isDesktop()) return;
          this._clearHoverCloseTimer();
          if (!this.modal?.classList.contains('sa-line-popup__modal--open')) {
            this._hoverOpenTimer = setTimeout(() => {
              this._openedByHover = true;
              this.openModal();
            }, 100);
          }
        });

        this.triggerBtn.addEventListener('mouseleave', () => {
          this._clearHoverOpenTimer();
          if (this._openedByHover) {
            this._startHoverCloseTimer();
          }
        });
      }

      // While mouse is over the modal, cancel any pending hover-close
      if (this.modal) {
        this.modal.addEventListener('mouseenter', () => {
          this._clearHoverCloseTimer();
        });

        this.modal.addEventListener('mouseleave', () => {
          if (this._openedByHover) {
            this._startHoverCloseTimer();
          }
        });
      }

      if (this.closeBtn) this.closeBtn.addEventListener('click', this.closeModal);
      if (this.overlay) this.overlay.addEventListener('click', this.closeModal);
      if (this.noShowInput) this.noShowInput.addEventListener('change', this.handleNoShow);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('keydown', this.handleKeydown);
      if (this._scrollTimer) clearTimeout(this._scrollTimer);
      this._clearHoverOpenTimer();
      this._clearHoverCloseTimer();
      document.body.style.overflow = '';
    }

    // Detects true pointer device (desktop), excludes touch-only devices
    _isDesktop() {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    _clearHoverOpenTimer() {
      if (this._hoverOpenTimer) {
        clearTimeout(this._hoverOpenTimer);
        this._hoverOpenTimer = null;
      }
    }

    _clearHoverCloseTimer() {
      if (this._hoverCloseTimer) {
        clearTimeout(this._hoverCloseTimer);
        this._hoverCloseTimer = null;
      }
    }

    // 200ms grace period so mouse can move from trigger to modal without closing
    _startHoverCloseTimer() {
      this._clearHoverCloseTimer();
      this._hoverCloseTimer = setTimeout(() => {
        this.closeModal();
      }, 200);
    }

    handleScroll() {
      if (!this.trigger) return;
      this.trigger.classList.add('sa-line-popup__trigger--scrolling');
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        this.trigger.classList.remove('sa-line-popup__trigger--scrolling');
      }, 300);
    }

    openModal() {
      if (!this.modal) return;
      if (this.modal.classList.contains('sa-line-popup__modal--open')) return;

      this._lastFocused = document.activeElement;

      this.modal.removeAttribute('inert');
      this.modal.removeAttribute('aria-hidden');
      if (this.triggerBtn) this.triggerBtn.setAttribute('aria-expanded', 'true');

      // Don't lock body scroll in hover mode (non-intrusive tooltip-like behaviour)
      if (!this._openedByHover) {
        document.body.style.overflow = 'hidden';
      }

      requestAnimationFrame(() => {
        this.modal.classList.add('sa-line-popup__modal--open');
        if (this.overlay) this.overlay.classList.add('sa-line-popup__overlay--open');
        // Only shift focus when opened by click (hover mode keeps focus in place)
        if (!this._openedByHover) {
          const focusable = this.getFocusableElements();
          if (focusable.length) focusable[0].focus();
        }
      });

      document.addEventListener('keydown', this.handleKeydown);
    }

    closeModal() {
      if (!this.modal) return;
      if (!this.modal.classList.contains('sa-line-popup__modal--open')) return;

      this._openedByHover = false;
      this._clearHoverCloseTimer();

      this.modal.classList.remove('sa-line-popup__modal--open');
      if (this.overlay) this.overlay.classList.remove('sa-line-popup__overlay--open');

      this.modal.setAttribute('inert', '');
      this.modal.setAttribute('aria-hidden', 'true');
      if (this.triggerBtn) this.triggerBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.handleKeydown);

      if (this._lastFocused) this._lastFocused.focus();
    }

    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.closeModal();
        return;
      }
      if ((e.key === 'Enter' || e.key === ' ') && document.activeElement?.type === 'checkbox') {
        e.preventDefault();
        document.activeElement.click();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = this.getFocusableElements();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    getFocusableElements() {
      if (!this.modal) return [];
      return Array.from(
        this.modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
    }

    handleNoShow() {
      if (!this.noShowInput || window.Shopify?.designMode) return;
      const storageKey = `sa-line-popup-hidden-${this.dataset.sectionId}`;
      if (this.noShowInput.checked) {
        sessionStorage.setItem(storageKey, 'true');
      } else {
        sessionStorage.removeItem(storageKey);
      }
    }
  }

  customElements.define('sa-line-popup', SaLinePopup);
}
